import { ReactNode, useEffect, useState } from "react";
import { Link, NavLink, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  Building2,
  Gauge,
  Bell,
  Users as UsersIcon,
  Settings,
  FileText,
  ClipboardList,
  Activity,
  ShieldCheck,
  Menu,
  X,
  LogOut,
  Home,
  ChevronLeft,
} from "lucide-react";
import { useAuthStore } from "@/store/auth";
import { orgApi } from "@/api/endpoints";
import { customerMsalInstance, staffMsalInstance } from "@/auth/msalConfig";
import type { Organisation, User } from "@/types";
import GlobalSearch from "./GlobalSearch";

interface NavItem {
  to: string;
  label: string;
  icon: ReactNode;
  show: (user: User) => boolean;
}

const platformStaffNav: NavItem[] = [
  { to: "/staff", label: "Overview", icon: <Activity size={18} />, show: () => true },
  { to: "/staff/organisations", label: "Organisations", icon: <Building2 size={18} />, show: () => true },
  { to: "/staff/audit", label: "System audit", icon: <ClipboardList size={18} />, show: () => true },
];

function orgNavFor(basePath: string, isStaff: boolean): NavItem[] {
  return [
    { to: `${basePath}`, label: "Dashboard", icon: <Home size={18} />, show: () => true },
    { to: `${basePath}/buildings`, label: "Buildings", icon: <Building2 size={18} />, show: () => true },
    { to: `${basePath}/readings`, label: "Readings", icon: <Gauge size={18} />,
      show: (u) => isStaff || !!u.role?.can_enter_readings },
    { to: `${basePath}/alerts`, label: "Alerts", icon: <Bell size={18} />,
      show: (u) => isStaff || !!u.role?.can_view_alerts },
    { to: `${basePath}/users`, label: "Users", icon: <UsersIcon size={18} />,
      show: (u) => isStaff || !!u.role?.can_manage_users },
    { to: `${basePath}/invoices`, label: "Invoices", icon: <FileText size={18} />, show: () => true },
    { to: `${basePath}/audit`, label: "Audit log", icon: <ClipboardList size={18} />,
      show: (u) => isStaff || !!u.role?.can_manage_users },
    { to: `${basePath}/settings`, label: "Settings", icon: <Settings size={18} />, show: () => true },
  ];
}

export default function AppLayout({ children }: { children: ReactNode }) {
  const user = useAuthStore((s) => s.user)!;
  const logout = useAuthStore((s) => s.logout);
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams<{ orgId?: string }>();
  const [open, setOpen] = useState(false);
  const [contextOrgName, setContextOrgName] = useState<string | null>(null);

  // Detect staff being inside an org context: /staff/organisations/:orgId/*
  const staffInOrg = location.pathname.startsWith("/staff/organisations/") && !!params.orgId;
  const inStaffPlatform = location.pathname.startsWith("/staff") && !staffInOrg;

  // Load the org name when staff drills into a specific org
  useEffect(() => {
    if (staffInOrg && params.orgId) {
      orgApi.get(params.orgId).then((o: Organisation) => setContextOrgName(o.name)).catch(() => setContextOrgName(null));
    } else {
      setContextOrgName(null);
    }
  }, [staffInOrg, params.orgId]);

  let items: NavItem[];
  let backLink: { to: string; label: string } | null = null;
  if (staffInOrg && params.orgId) {
    items = orgNavFor(`/staff/organisations/${params.orgId}`, true).filter((n) => n.show(user));
    backLink = { to: "/staff/organisations", label: "Back to organisations" };
  } else if (inStaffPlatform) {
    items = platformStaffNav;
  } else {
    items = orgNavFor("", false).filter((n) => n.show(user));
    // Rewrite the dashboard "to" to /dashboard for the org context
    items = items.map((n) => (n.to === "" ? { ...n, to: "/dashboard" } : n));
  }

  const handleLogout = async () => {
    // Clear MSAL caches so the next sign-in doesn't auto-select the previous account.
    // Clearing both customer + staff is harmless if one wasn't in use.
    try {
      await Promise.all([
        customerMsalInstance.clearCache().catch(() => {}),
        staffMsalInstance.clearCache().catch(() => {}),
      ]);
    } catch {
      /* swallow — defensive cleanup below */
    }
    // Defensive: remove any leftover MSAL keys that didn't go away
    [
      ...Object.keys(sessionStorage),
      ...Object.keys(localStorage),
    ].forEach((k) => {
      if (k.startsWith("msal.") || k.includes("login.microsoftonline") || k.includes("ciamlogin")) {
        sessionStorage.removeItem(k);
        localStorage.removeItem(k);
      }
    });
    logout();
    navigate("/", { replace: true });
  };

  return (
    <div className="min-h-screen flex bg-slate-50">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-40 w-64 bg-white border-r border-slate-200 transform transition-transform ${
          open ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static lg:flex lg:flex-col`}
      >
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <Link to={user.is_staff ? "/staff" : "/dashboard"} className="flex items-center gap-2">
            <div className="h-8 w-8 rounded-lg bg-brand-700 flex items-center justify-center">
              <Building2 className="text-white" size={18} />
            </div>
            <span className="font-semibold text-slate-900">UmdoniWay</span>
          </Link>
          <button className="lg:hidden" onClick={() => setOpen(false)} aria-label="Close menu">
            <X size={20} />
          </button>
        </div>

        {/* Global search for staff */}
        {user.is_staff && (
          <div className="px-3 pt-3">
            <GlobalSearch />
          </div>
        )}

        {/* Staff-in-org context banner */}
        {backLink && (
          <div className="px-3 pt-3">
            <Link
              to={backLink.to}
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-brand-700 mb-1"
            >
              <ChevronLeft size={14} />
              {backLink.label}
            </Link>
            <div className="rounded-lg bg-brand-50 px-3 py-2">
              <p className="text-xs text-brand-700 font-medium">Acting on behalf of</p>
              <p className="text-sm font-semibold text-brand-900 truncate" title={contextOrgName ?? ""}>
                {contextOrgName ?? "Loading…"}
              </p>
            </div>
          </div>
        )}

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.to === "/dashboard" || item.to === "/staff" || (!!backLink && item.to === backLink.to.replace("/organisations", `/organisations/${params.orgId}`))}
              onClick={() => setOpen(false)}
              className={({ isActive }) =>
                `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition ${
                  isActive
                    ? "bg-brand-50 text-brand-700"
                    : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                }`
              }
            >
              {item.icon}
              {item.label}
            </NavLink>
          ))}
        </nav>

        <div className="p-3 border-t border-slate-200">
          <div className="px-3 py-2 text-xs text-slate-500 truncate">{user.email}</div>
          {user.role && <div className="px-3 pb-2 text-xs text-slate-400">{user.role.name}</div>}
          {user.is_staff && (
            <div className="px-3 pb-2">
              <span className="badge bg-brand-100 text-brand-800">
                <ShieldCheck size={12} /> Staff
              </span>
            </div>
          )}
          <button onClick={handleLogout} className="btn-secondary w-full justify-start" type="button">
            <LogOut size={16} /> Sign out
          </button>
        </div>
      </aside>

      {/* Mobile overlay */}
      {open && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="lg:hidden flex items-center justify-between p-4 border-b border-slate-200 bg-white">
          <button onClick={() => setOpen(true)} aria-label="Open menu"><Menu size={22} /></button>
          <span className="font-semibold text-slate-900">UmdoniWay</span>
          <span className="w-6" />
        </header>
        <main className="flex-1 p-4 lg:p-8 overflow-x-auto">{children}</main>
      </div>
    </div>
  );
}
